import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import {
    LANGUAGES,
    translations as staticTranslations,
} from "../../i18n/translations";

const CUSTOM_LANGS_KEY = "vblive-custom-langs";
const CURRENT_LANG_KEY = "vblive-lang";

function loadCustomLangs() {
  try {
    return JSON.parse(localStorage.getItem(CUSTOM_LANGS_KEY) ?? "[]");
  } catch {
    return [];
  }
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(
    localStorage.getItem(CURRENT_LANG_KEY) || "en"
  );
  const [customLangs, setCustomLangs] = useState(loadCustomLangs);

  const setLang = (newLang) => {
    setLangState(newLang);
    localStorage.setItem(CURRENT_LANG_KEY, newLang);
  };

  /** Merged language metadata: built-in + custom. */
  const allLanguages = useMemo(() => {
    const merged = { ...LANGUAGES };
    customLangs.forEach(({ langCode, langName, flag }) => {
      merged[langCode] = { label: langName, flag: flag || "🌐" };
    });
    return merged;
  }, [customLangs]);

  /** Merged translations: built-in + custom. */
  const allTranslations = useMemo(() => {
    const merged = { ...staticTranslations };
    customLangs.forEach(({ langCode, translations: t }) => {
      merged[langCode] = t;
    });
    return merged;
  }, [customLangs]);

  /** Translate a key, falling back to the key itself (English). */
  const t = useCallback(
    (key) => allTranslations[lang]?.[key] ?? key,
    [lang, allTranslations]
  );

  /** Register a new custom language so it is immediately usable in the picker. */
  const registerLanguage = useCallback((pkg) => {
    const { langCode, langName, flag, translations: pkgTranslations } = pkg;
    setCustomLangs((prev) => {
      const filtered = prev.filter((c) => c.langCode !== langCode);
      const next = [
        ...filtered,
        {
          langCode,
          langName,
          flag: flag || "🌐",
          translations: pkgTranslations,
        },
      ];
      localStorage.setItem(CUSTOM_LANGS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  /** Replace all custom languages with the given list (used to sync with Firestore). */
  const syncCustomLanguages = useCallback((pkgs) => {
    const next = pkgs.map(({ langCode, langName, flag, translations: t }) => ({
      langCode,
      langName,
      flag: flag || "🌐",
      translations: t,
    }));
    localStorage.setItem(CUSTOM_LANGS_KEY, JSON.stringify(next));
    setCustomLangs(next);
  }, []);

  return (
    <LanguageContext.Provider
      value={{
        lang,
        setLang,
        t,
        allLanguages,
        registerLanguage,
        syncCustomLanguages,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return ctx;
}
